import React, { useState } from "react";
import { StyleSheet, View, Platform, Dimensions } from "react-native";
import LHSectionHeader from "../../components/LHSectionHeader";
import LHOptionCard from "../../components/LHOptionCard";
import SparcWalkInspectionImageUpload from "./SparcWalkInspectionImageUpload";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import FontFamily from "../../constants/fonts";
import { useTheme } from "../../theme";

// Helper to detect iPad
const isIPad = Platform.OS === "ios" && Dimensions.get("window").width >= 768;

type Option = {
  id?: string | number;
  ID?: string | number;
  QuestionId?: string | number;
  label?: string;
  Question?: string;
  Details?: string | null;
  Detail_Link?: string | null;
};

type Props = {
  title: string;
  options?: Option[];
  isDisabled?: boolean;
  selectedIds?: string[];
  onSelect?: (id: string) => void;
  error?: boolean;
  testID?: string;
};

const SparcWalkInspectionQuestions: React.FC<Props> = ({
  title,
  options = [],
  isDisabled = false,
  selectedIds = [],
  onSelect,
  error = false,
  testID,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDarkTheme = resolvedTheme === "dark";
  const [imageUploadModalVisible, setImageUploadModalVisible] =
    useState<boolean>(false);
  const [selectedQuestion, setSelectedQuestion] = useState<Option | null>(null);
  const sparcImages = useSelector(
    (state: RootState) => state.sparcWalkInspection.images
  );

  const getOptionId = (opt: Option) =>
    (opt?.QuestionId ?? opt?.ID ?? opt?.id)?.toString?.() ?? "";

  const handleSelect = (id: string) => {
    if (onSelect) {
      onSelect(id);
    }
  };

  const handleCameraPress = (option: Option): void => {
    setSelectedQuestion(option);
    setImageUploadModalVisible(true);
  };

  const hasUploadedImages = (optionId: string): boolean =>
    sparcImages.some((img) => img.observationId === optionId);

  const getDisplayTitle = (opt: Option): string =>
    opt.Question || opt.label || "Untitled";

  const getDetailsText = (opt: Option): string =>
    opt.Details || opt.Detail_Link || "";

  return (
    <View
      style={[styles.container, error && styles.errorContainer]}
      testID={testID}
    >
      {/* Card with integrated header and body (matches SparcWalkInspectionActivity) */}
      <View style={styles.cardContainer}>
        <LHSectionHeader
          title={title}
          containerStyle={styles.sectionHeader}
          textStyle={styles.header1text}
        />

        <View
          style={[
            styles.optionGrid,
            styles.optionCardWrapper,
            isDarkTheme && {
              backgroundColor: colors.background.surface,
              borderColor: colors.border.default,
            },
            {
              justifyContent: isIPad
                ? "flex-start"
                : Platform.OS === "web"
                ? "flex-start"
                : "space-between",
            },
          ]}
        >
          {options.map((opt) => {
            const optionId = getOptionId(opt);
            const hasImages = hasUploadedImages(optionId);

            return (
              <LHOptionCard
                key={optionId}
                title={getDisplayTitle(opt)}
                selected={selectedIds.includes(optionId)}
                disabled={isDisabled}
                onPress={() => !isDisabled && handleSelect(optionId)}
                onCameraPress={() => handleCameraPress(opt)}
                hasUploadedImage={hasImages}
                detailsText={getDetailsText(opt)}
              />
            );
          })}
        </View>
      </View>

      {/* Image Upload Modal */}
      {selectedQuestion && (
        <SparcWalkInspectionImageUpload
          visible={imageUploadModalVisible}
          onClose={() => setImageUploadModalVisible(false)}
          activityId={getOptionId(selectedQuestion)}
          activityLabel={getDisplayTitle(selectedQuestion)}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: "100%",
    marginTop: 24,
  },
  errorContainer: {
    borderColor: "#dc3545",
    borderWidth: 1,
  },
  cardContainer: {
    borderRadius: 6,
    // allow header to expand beyond original bounds when wrapping
    overflow: Platform.OS === "ios" ? "visible" : "hidden",
  },
  sectionHeader: {
    backgroundColor: "#3582E6",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    // use minHeight so header can expand to 2 lines when needed
    minHeight: 48,
  },
  header1text: {
    fontSize: 14,
    fontWeight: "700",
    lineHeight: 20,
    fontFamily: FontFamily.bold,
  },
  optionGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    // justifyContent is now set dynamically in the component
    gap: Platform.OS === "web" ? 16 : 6,
    borderColor: "#e9ecef",
    borderWidth: 1,
    borderTopWidth: 0,
    borderRadius: 6,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    padding: 16,
  },
  optionCardWrapper: {
    backgroundColor: "white",
  },
});

export default SparcWalkInspectionQuestions;
